import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Plus, X, Loader2, Trash2, AlertTriangle } from "lucide-react";

export function AddConsultantDialog({ open, onOpenChange, form, setForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <UserPlus className="w-5 h-5 mr-2 text-purple-600" />
            Add New Consultant
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" data-testid="consultant-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Email Address</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" data-testid="consultant-email-input" />
          </div>
          <div className="space-y-2">
            <Label>Temporary Password</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Create a password" data-testid="consultant-password-input" />
            <p className="text-xs text-neutral-500">Share this password with the new consultant. They can change it after logging in.</p>
          </div>
          <Button onClick={onSubmit} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="submit-add-consultant-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add Consultant
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AssignConsultantDialog({ open, onOpenChange, client, allConsultants, onAssign, onUnassign, setClient }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Consultants for {client?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div>
            <Label className="text-sm font-medium text-neutral-700">Assigned Consultants</Label>
            {client?.assigned_consultants_info?.length > 0 ? (
              <div className="mt-2 space-y-2">
                {client.assigned_consultants_info.map((consultant) => (
                  <div key={consultant.user_id} className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center text-sm font-medium text-blue-700">
                        {consultant.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{consultant.name}</p>
                        <p className="text-xs text-neutral-500">{consultant.email}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onUnassign(client.user_id, consultant.user_id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 mt-2">No consultants assigned yet</p>
            )}
          </div>
          <div>
            <Label className="text-sm font-medium text-neutral-700">Add Consultant</Label>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              {allConsultants
                .filter(c => !client?.assigned_consultants_info?.some(ac => ac.user_id === c.user_id))
                .map((consultant) => (
                  <div key={consultant.user_id} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg hover:bg-neutral-100">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-medium text-neutral-700">
                        {consultant.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{consultant.name}</p>
                        <p className="text-xs text-neutral-500">{consultant.email}</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => {
                        onAssign(client.user_id, consultant.user_id);
                        setClient(prev => ({
                          ...prev,
                          assigned_consultants_info: [...(prev.assigned_consultants_info || []), consultant]
                        }));
                      }}>
                      <Plus className="w-3 h-3 mr-1" /> Assign
                    </Button>
                  </div>
                ))}
              {allConsultants.filter(c => !client?.assigned_consultants_info?.some(ac => ac.user_id === c.user_id)).length === 0 && (
                <p className="text-sm text-neutral-500 text-center py-2">All consultants are already assigned</p>
              )}
            </div>
          </div>
          <Button className="w-full bg-[#333333] hover:bg-[#444444] text-white" onClick={() => onOpenChange(false)}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteClientDialog({ open, onOpenChange, client, onDelete, processingAction }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center text-red-600">
            <AlertTriangle className="w-5 h-5 mr-2" /> Delete Client
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete the client
            <strong> {client?.name}</strong> and all their data including projects, notes, logs, and reports.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onDelete} disabled={processingAction === client?.user_id}>
            {processingAction === client?.user_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete Permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConsultantDialog({ open, onOpenChange, consultant, onDelete, processingAction }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center text-red-600">
            <AlertTriangle className="w-5 h-5 mr-2" /> Remove Consultant
          </DialogTitle>
          <DialogDescription>
            This will remove <strong>{consultant?.name}</strong> from the platform
            and unassign them from all clients. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="destructive" onClick={onDelete} disabled={processingAction === consultant?.user_id}>
            {processingAction === consultant?.user_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Remove Consultant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
